/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
import { defineAsyncComponent } from 'vue'

// Lightweight components - load immediately
export { default as TextBlock } from './TextBlock.vue'
export { default as ButtonBlock } from './ButtonBlock.vue'

// Heavy components - lazy load for better performance
export const TextAndImageBlock = defineAsyncComponent(() => import('./TextAndImageBlock.vue'))
export const TwoTextsBlock = defineAsyncComponent(() => import('./TwoTextsBlock.vue'))
export const ImageBlock = defineAsyncComponent(() => import('./ImageBlock.vue'))
export const VideoBlock = defineAsyncComponent(() => import('./VideoBlock.vue'))
export const TableBlock = defineAsyncComponent(() => import('./TableBlock.vue'))
export const MapBlock = defineAsyncComponent(() => import('./MapBlock.vue'))
export const CoverBlock = defineAsyncComponent(() => import('./CoverBlock.vue'))
export const CardsGridBlock = defineAsyncComponent(() => import('./CardsGridBlock.vue'))
export const CarouselBlock = defineAsyncComponent(() => import('./CarouselBlock.vue'))

// Import lightweight components directly
import TextBlockSync from './TextBlock.vue'
import ButtonBlockSync from './ButtonBlock.vue'
import UnknownBlockSync from './UnknownBlock.vue'

// Export the UnknownBlock for direct use
export { UnknownBlockSync as UnknownBlock }

// TypeScript interface for all block components
export interface BlockProps {
  block: {
    __typename: string
    id: string
    [key: string]: any
  }
}

// Memory-optimized component mapping for dynamic rendering
export const BLOCK_COMPONENTS = {
  // Lightweight blocks loaded immediately
  ComponentBlocksTextBlock: TextBlockSync,
  ComponentBlocksButtonBlock: ButtonBlockSync,
  
  // Heavy blocks loaded lazily with Suspense fallback
  ComponentBlocksTextAndImageBlock: defineAsyncComponent({
    loader: () => import('./TextAndImageBlock.vue'),
    loadingComponent: () => import('./LoadingBlock.vue'),
    errorComponent: UnknownBlockSync,
    delay: 200,
    timeout: 10000
  }),
  
  ComponentBlocksTwoTextsBlock: defineAsyncComponent({
    loader: () => import('./TwoTextsBlock.vue'),
    loadingComponent: () => import('./LoadingBlock.vue'),
    errorComponent: UnknownBlockSync,
    delay: 200,
    timeout: 10000
  }),
  
  ComponentBlocksImageBlock: defineAsyncComponent({
    loader: () => import('./ImageBlock.vue'),
    loadingComponent: () => import('./LoadingBlock.vue'),
    errorComponent: UnknownBlockSync,
    delay: 200,
    timeout: 10000
  }),
  
  ComponentBlocksVideoBlock: defineAsyncComponent({
    loader: () => import('./VideoBlock.vue'),
    loadingComponent: () => import('./LoadingBlock.vue'),
    errorComponent: UnknownBlockSync,
    delay: 200,
    timeout: 10000
  }),
  
  ComponentBlocksTableBlock: defineAsyncComponent({
    loader: () => import('./TableBlock.vue'),
    loadingComponent: () => import('./LoadingBlock.vue'),
    errorComponent: UnknownBlockSync,
    delay: 200,
    timeout: 10000
  }),
  
  ComponentBlocksMapBlock: defineAsyncComponent({
    loader: () => import('./MapBlock.vue'),
    loadingComponent: () => import('./LoadingBlock.vue'),
    errorComponent: UnknownBlockSync,
    delay: 200,
    timeout: 10000
  }),
  
  ComponentBlocksCoverBlock: defineAsyncComponent({
    loader: () => import('./CoverBlock.vue'),
    loadingComponent: () => import('./LoadingBlock.vue'),
    errorComponent: UnknownBlockSync,
    delay: 200,
    timeout: 10000
  }),
  
  ComponentBlocksCardsGridBlock: defineAsyncComponent({
    loader: () => import('./CardsGridBlock.vue'),
    loadingComponent: () => import('./LoadingBlock.vue'),
    errorComponent: UnknownBlockSync,
    delay: 200,
    timeout: 10000
  }),
  
  ComponentBlocksCarouselBlock: defineAsyncComponent({
    loader: () => import('./CarouselBlock.vue'),
    loadingComponent: () => import('./LoadingBlock.vue'),
    errorComponent: UnknownBlockSync,
    delay: 200,
    timeout: 10000
  })
} as const 